// File: src/components/layout/DisclaimerModal.js
/**
 * Disclaimer Modal
 *
 * A one-time liability notice shown on a visitor's first load. It makes clear
 * that this viewer is an unofficial convenience copy of the OSHA regulations
 * and that the authoritative, legally binding text lives at osha.gov / eCFR —
 * so we are not on the hook if anything here is wrong or out of date.
 *
 * The acknowledgment is stored in localStorage, so the notice appears only
 * once per device. The stored value is version-stamped: bump
 * DISCLAIMER_VERSION after any material change to the wording to re-prompt
 * everyone who previously dismissed it.
 */

import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, ExternalLink } from 'lucide-react';
import { getItem, setItem } from '../../utils/storage';
import { STORAGE_KEYS } from '../../utils/constants';
import '../../styles/components/disclaimer.css';

// Keep this as a non-numeric string (it round-trips through storage as text).
// Bump it when the disclaimer text changes so prior acknowledgments expire.
const DISCLAIMER_VERSION = '2026-09-08';

// Official source the notice points people to.
const OSHA_URL = 'https://www.osha.gov/laws-regs/regulations/standardnumber';

const DisclaimerModal = () => {
  const [open, setOpen] = useState(false);
  const dialogRef = useRef(null);
  const acceptRef = useRef(null);

  // Decide on mount whether the notice still needs to be shown.
  useEffect(() => {
    if (getItem(STORAGE_KEYS.DISCLAIMER_ACK, null) !== DISCLAIMER_VERSION) {
      setOpen(true);
    }
  }, []);

  // While open, lock background scroll and move focus into the dialog.
  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    acceptRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  const acknowledge = () => {
    setItem(STORAGE_KEYS.DISCLAIMER_ACK, DISCLAIMER_VERSION);
    setOpen(false);
  };

  // Keep keyboard focus trapped within the dialog (a waiver should be
  // acknowledged deliberately, so there is no backdrop / Escape dismissal).
  const handleKeyDown = (event) => {
    if (event.key !== 'Tab') return;

    const focusable = dialogRef.current?.querySelectorAll('a[href], button');
    if (!focusable || focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  if (!open) return null;

  return (
    <div className="disclaimer-overlay" role="presentation">
      <div
        className="disclaimer-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="disclaimer-title"
        aria-describedby="disclaimer-body"
        ref={dialogRef}
        onKeyDown={handleKeyDown}
      >
        <div className="disclaimer-header">
          <span className="disclaimer-icon" aria-hidden="true">
            <AlertTriangle size={22} />
          </span>
          <h2 className="disclaimer-title" id="disclaimer-title">
            For reference only
          </h2>
        </div>

        <div className="disclaimer-body" id="disclaimer-body">
          <p>
            This tool reproduces OSHA regulations — 29 CFR Parts 1902
            through 1928, as listed in this tool — for your convenience only.
            It is <strong>not</strong> an official source and may be
            incomplete, out of date, or contain errors.
          </p>
          <p>
            The official, legally binding text is published by the U.S.
            government. Always verify against the current regulations at{' '}
            <a
              className="disclaimer-link"
              href={OSHA_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              osha.gov
              <ExternalLink size={14} aria-hidden="true" />
            </a>{' '}
            before relying on anything here for a compliance decision.
          </p>
          <p className="disclaimer-fineprint">
            By continuing you acknowledge that Critical Dynamics Risk Management
            and the providers of this tool accept no liability for any errors,
            omissions, or actions taken based on the information shown here.
          </p>
        </div>

        <div className="disclaimer-actions">
          <a
            className="disclaimer-secondary"
            href={OSHA_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            View official regulations
          </a>
          <button
            type="button"
            className="disclaimer-accept"
            onClick={acknowledge}
            ref={acceptRef}
          >
            I understand
          </button>
        </div>
      </div>
    </div>
  );
};

export default DisclaimerModal;
